import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  addEdge,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

// ── Custom node components ────────────────────────────────────────────────────

function SendMessageNode({ id, data, selected }) {
  const subtypeLabels = { text: 'Texto', list: 'Lista', button: 'Botones', product: 'Producto', boton_cta: 'Botón CTA', meta_flow: 'Flujo Meta', carousel: 'Carrusel' };
  return (
    <div style={{ background: '#1e3a8a', border: `2px solid ${selected ? '#93c5fd' : '#1e40af'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#fff', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#93c5fd' }} />
      <div style={{ fontSize: '0.68rem', color: '#93c5fd', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Enviar Mensaje</div>
      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{subtypeLabels[data.subtype] || 'Texto'}</div>
      {data.text && (
        <div style={{ fontSize: '0.73rem', color: '#bfdbfe', marginTop: '4px', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.text}</div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: '#93c5fd' }} />
    </div>
  );
}

function WaitResponseNode({ id, selected }) {
  return (
    <div style={{ background: '#f8fafc', border: `2px solid ${selected ? '#38bdf8' : '#cbd5e1'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#1e293b', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(0,0,0,0.25)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#94a3b8' }} />
      <div style={{ fontSize: '0.68rem', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Esperar Respuesta</div>
      <div style={{ fontSize: '0.82rem', color: '#475569' }}>Pausando flujo...</div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#94a3b8' }} />
    </div>
  );
}

function SwitchNode({ id, data, selected }) {
  const cases = data.cases || [];
  const regularCount = cases.filter(c => c.id !== 'default').length;
  return (
    <div style={{ background: '#7c2d12', border: `2px solid ${selected ? '#fdba74' : '#ea580c'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#fff', position: 'relative', paddingBottom: '22px' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#fdba74' }} />
      <div style={{ fontSize: '0.68rem', color: '#fdba74', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Tomar decisión</div>
      <div style={{ fontSize: '0.88rem', fontWeight: 600, marginBottom: '5px' }}>{regularCount} caso{regularCount !== 1 ? 's' : ''}</div>
      {cases.map((c) => (
        <div key={c.id} style={{
          fontSize: '0.73rem',
          color: c.id === 'default' ? '#ea580c' : '#fed7aa',
          background: c.id === 'default' ? 'rgba(234,88,12,0.2)' : 'rgba(255,255,255,0.1)',
          borderRadius: '4px', padding: '2px 6px', marginBottom: '2px',
        }}>
          {c.label || c.value || '(caso)'}
        </div>
      ))}
      {cases.length === 0 ? (
        <Handle type="source" position={Position.Bottom} style={{ background: '#fdba74' }} />
      ) : (
        cases.map((c, i) => (
          <Handle
            key={c.id}
            type="source"
            position={Position.Bottom}
            id={c.id}
            style={{ background: '#fdba74', left: `${((i + 0.5) / cases.length) * 100}%` }}
          />
        ))
      )}
    </div>
  );
}

function EnvioNode({ id, data, selected }) {
  return (
    <div style={{ background: '#1e1b4b', border: `2px solid ${selected ? '#a5b4fc' : '#4f46e5'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#fff', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#a5b4fc' }} />
      <div style={{ fontSize: '0.68rem', color: '#a5b4fc', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Envío a Flujo</div>
      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{data.targetFlowName || '(sin flujo)'}</div>
      {data.targetNodeLabel && (
        <div style={{ fontSize: '0.73rem', color: '#c7d2fe', marginTop: '3px', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.targetNodeLabel}</div>
      )}
    </div>
  );
}

function EscalateNode({ id, data, selected }) {
  return (
    <div style={{ background: '#4c1d95', border: `2px solid ${selected ? '#c4b5fd' : '#7c3aed'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#fff', position: 'relative', paddingBottom: '24px' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#c4b5fd' }} />
      <div style={{ fontSize: '0.68rem', color: '#c4b5fd', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Escalar a Agente</div>
      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{data.campaignLabel || '(sin campaña)'}</div>
      <div style={{ marginTop: '8px', fontSize: '0.65rem', color: '#fbbf24', textAlign: 'center' }}>⏰ Fuera de horario</div>
      <Handle type="source" position={Position.Bottom} id="outOfHours" style={{ background: '#fbbf24' }} />
    </div>
  );
}

function SaveToDbNode({ id, data, selected }) {
  return (
    <div style={{ background: '#166534', border: `2px solid ${selected ? '#86efac' : '#16a34a'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#fff', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#86efac' }} />
      <div style={{ fontSize: '0.68rem', color: '#86efac', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Guardar en BD</div>
      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{data.fieldName || '(sin campo)'}</div>
      {data.sourceType && (
        <div style={{ fontSize: '0.73rem', color: '#bbf7d0', marginTop: '4px' }}>{data.sourceType}</div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: '#86efac' }} />
    </div>
  );
}

function SendEmailNode({ id, data, selected }) {
  return (
    <div style={{ background: '#064e3b', border: `2px solid ${selected ? '#34d399' : '#059669'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#fff', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#34d399' }} />
      <div style={{ fontSize: '0.68rem', color: '#34d399', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Enviar Email</div>
      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{data.provider || 'Gmail'}</div>
      {data.fromEmail && (
        <div style={{ fontSize: '0.73rem', color: '#6ee7b7', marginTop: '4px', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.fromEmail}</div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: '#34d399' }} />
    </div>
  );
}

function PaymentGatewayNode({ id, data, selected }) {
  return (
    <div style={{ background: '#4c1d95', border: `2px solid ${selected ? '#a78bfa' : '#7c3aed'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#fff', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#a78bfa' }} />
      <div style={{ fontSize: '0.68rem', color: '#a78bfa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Pasarela de pagos</div>
      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{data.amountField ? `Monto: ${data.amountField}` : '(Sin configurar)'}</div>
      {data.urlField && <div style={{ fontSize: '0.73rem', color: '#c4b5fd', marginTop: '3px' }}>→ {data.urlField}</div>}
      <Handle type="source" position={Position.Bottom} style={{ background: '#a78bfa' }} />
    </div>
  );
}

function CreatePdfNode({ id, data, selected }) {
  return (
    <div style={{ background: '#78350f', border: `2px solid ${selected ? '#fcd34d' : '#d97706'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#fff', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#fcd34d' }} />
      <div style={{ fontSize: '0.68rem', color: '#fcd34d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Crear PDF</div>
      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{data.templateName || '(Sin plantilla)'}</div>
      {data.fieldName && (
        <div style={{ fontSize: '0.73rem', color: '#fde68a', marginTop: '3px' }}>→ {data.fieldName}</div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: '#fcd34d' }} />
    </div>
  );
}

function DomicilioNode({ id, data, selected }) {
  return (
    <div style={{ background: '#451a03', border: `2px solid ${selected ? '#fbbf24' : '#d97706'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#fff', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#fbbf24' }} />
      <div style={{ fontSize: '0.68rem', color: '#fbbf24', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Domicilio</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{data.dept_field ? `Depto: ${data.dept_field}` : '(sin configurar)'}</div>
      {data.fee_field && <div style={{ fontSize: '0.72rem', color: '#fed7aa', marginTop: '2px' }}>→ {data.fee_field}</div>}
      <Handle type="source" position={Position.Bottom} style={{ background: '#fbbf24' }} />
    </div>
  );
}

function DireccionNode({ id, data, selected }) {
  return (
    <div style={{ background: '#2d1b69', border: `2px solid ${selected ? '#ddd6fe' : '#7c3aed'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#fff', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#ddd6fe' }} />
      <div style={{ fontSize: '0.68rem', color: '#ddd6fe', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Dirección</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{data.address_field ? `Campo: ${data.address_field}` : '(sin configurar)'}</div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#ddd6fe' }} />
    </div>
  );
}

function CalculatorNode({ id, data, selected }) {
  const opLabel = { suma: '+', resta: '−', multiplicacion: '×', division: '÷' };
  return (
    <div style={{ background: '#042f2e', border: `2px solid ${selected ? '#67e8f9' : '#0891b2'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#fff', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#67e8f9' }} />
      <div style={{ fontSize: '0.68rem', color: '#67e8f9', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Calculadora</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>
        {data.field1 || '?'} {opLabel[data.operation] || '?'} {data.field2 || '?'}
      </div>
      {data.result_field && <div style={{ fontSize: '0.72rem', color: '#a5f3fc', marginTop: '2px' }}>→ {data.result_field}</div>}
      <Handle type="source" position={Position.Bottom} style={{ background: '#67e8f9' }} />
    </div>
  );
}

function BookAppointmentNode({ id, data, selected }) {
  return (
    <div style={{ background: '#134e4a', border: `2px solid ${selected ? '#5eead4' : '#0d9488'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '168px', color: '#fff', position: 'relative', paddingBottom: '24px' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#5eead4' }} />
      <div style={{ fontSize: '0.68rem', color: '#5eead4', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Reservar Cita</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{data.service_id || '(sin servicio)'}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '0.65rem' }}>
        <span style={{ color: '#22c55e' }}>✓ Éxito</span>
        <span style={{ color: '#f87171' }}>✗ Fallo</span>
      </div>
      <Handle type="source" position={Position.Bottom} id="success" style={{ background: '#22c55e', left: '28%' }} />
      <Handle type="source" position={Position.Bottom} id="fail" style={{ background: '#f87171', left: '72%' }} />
    </div>
  );
}

function AvailableSlotsNode({ id, data, selected }) {
  return (
    <div style={{ background: '#1e3a5f', border: `2px solid ${selected ? '#93c5fd' : '#2563eb'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '168px', color: '#fff', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#93c5fd' }} />
      <div style={{ fontSize: '0.68rem', color: '#93c5fd', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Citas Disponibles</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{data.service_id || '(sin servicio)'}</div>
      <div style={{ fontSize: '0.72rem', color: '#bfdbfe', marginTop: '3px' }}>{data.date_source === 'field' ? `📅 campo: ${data.date_field || '?'}` : '📅 hoy'}</div>
      <Handle type="source" position={Position.Bottom} style={{ background: '#93c5fd' }} />
    </div>
  );
}

function TicketsNode({ id, data, selected }) {
  return (
    <div style={{ background: '#7c2d12', border: `2px solid ${selected ? '#fdba74' : '#ea580c'}`, borderRadius: '10px', padding: '10px 16px', minWidth: '160px', color: '#fff', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#fdba74' }} />
      <div style={{ fontSize: '0.68rem', color: '#fdba74', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Crear Ticket</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{data.title_field ? `Título: ${data.title_field}` : '(sin configurar)'}</div>
      {data.name_field && <div style={{ fontSize: '0.72rem', color: '#fed7aa', marginTop: '2px' }}>👤 {data.name_field}</div>}
      <Handle type="source" position={Position.Bottom} style={{ background: '#fdba74' }} />
    </div>
  );
}

function SmartBoxNode({ id, data, selected }) {
  return (
    <div style={{ background: '#4a044e', border: '2px solid ' + (selected ? '#f0abfc' : '#c026d3'), borderRadius: '10px', padding: '10px 16px', minWidth: '168px', color: '#fff', position: 'relative' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#f0abfc' }} />
      <div style={{ fontSize: '0.68rem', color: '#f0abfc', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>Caja Inteligente</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>
        {data.answer_field ? 'Responde en: ' + data.answer_field : '(sin configurar)'}
      </div>
      {data.model && (
        <div style={{ fontSize: '0.7rem', color: '#f5d0fe', marginTop: '2px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.model}</div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: '#f0abfc' }} />
    </div>
  );
}

function ApiRestNode({ id, data, selected }) {
  const method = (data.method || 'GET').toUpperCase();
  const url = (data.url || '').replace(/^https?:\/\//i, '');
  return (
    <div style={{ background: '#27272a', border: '2px solid ' + (selected ? '#e4e4e7' : '#71717a'), borderRadius: '10px', padding: '10px 16px', minWidth: '178px', color: '#fff', position: 'relative', paddingBottom: '24px' }}>
      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>#{id}</span>
      <Handle type="target" position={Position.Top} style={{ background: '#d4d4d8' }} />
      <div style={{ fontSize: '0.68rem', color: '#d4d4d8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>API REST</div>
      <div style={{ fontSize: '0.8rem', fontWeight: 600, maxWidth: '155px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <span style={{ color: '#fbbf24', fontFamily: 'monospace' }}>{method}</span>
        <span style={{ color: '#e4e4e7' }}>{' ' + (url || '(sin URL)')}</span>
      </div>
      {data.result_field && (
        <div style={{ fontSize: '0.7rem', color: '#a1a1aa', marginTop: '2px' }}>{'-> ' + data.result_field}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '0.65rem' }}>
        <span style={{ color: '#22c55e' }}>OK</span>
        <span style={{ color: '#f87171' }}>Error</span>
      </div>
      <Handle type="source" position={Position.Bottom} id="success" style={{ background: '#22c55e', left: '28%' }} />
      <Handle type="source" position={Position.Bottom} id="error" style={{ background: '#f87171', left: '72%' }} />
    </div>
  );
}

const nodeTypes = {
  sendMessage: SendMessageNode,
  waitResponse: WaitResponseNode,
  switch: SwitchNode,
  saveToDb: SaveToDbNode,
  escalate: EscalateNode,
  envio: EnvioNode,
  sendEmail: SendEmailNode,
  createPdf: CreatePdfNode,
  paymentGateway: PaymentGatewayNode,
  bookAppointment: BookAppointmentNode,
  availableSlots: AvailableSlotsNode,
  domicilio: DomicilioNode,
  direccion: DireccionNode,
  calculator: CalculatorNode,
  tickets: TicketsNode,
  smartBox: SmartBoxNode,
  apiRest: ApiRestNode,
};

// ── Config panel sub-components ───────────────────────────────────────────────

const inputSt = { width: '100%', padding: '6px 10px', background: '#0a0a0a', border: '1px solid #334155', borderRadius: '6px', color: '#e2e8f0', fontSize: '0.83rem', boxSizing: 'border-box', outline: 'none' };
const labelSt = { display: 'block', color: '#94a3b8', fontSize: '0.76rem', marginBottom: '4px' };
const sectionSt = { marginBottom: '0.9rem' };
const checkLabelSt = { ...labelSt, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', marginBottom: 0 };
const addBtnSt = { background: '#38bdf8', border: 'none', borderRadius: '4px', color: '#0f0f0f', fontWeight: 700, fontSize: '0.76rem', padding: '3px 10px', cursor: 'pointer' };
const delBtnSt = { background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '4px', fontSize: '0.73rem', padding: '2px 8px', cursor: 'pointer' };
const itemCardSt = { background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: '6px', padding: '8px', marginBottom: '6px' };

function SendMessageConfig({ data, onChange }) {
  const [docFields, setDocFields] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allForms, setAllForms] = useState([]);

  useEffect(() => {
    const needsProducts = data.subtype === 'product' || (data.subtype === 'carousel' && data.carouselMode === 'product');
    if (!needsProducts) return;
    const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    fetch('/api/products?page=1', { headers: h })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setAllProducts(Array.isArray(d.data) ? d.data : []))
      .catch(() => {});
  }, [data.subtype, data.carouselMode]);

  useEffect(() => {
    if (data.subtype !== 'meta_flow') return;
    const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    fetch('/api/forms?page=1', { headers: h })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setAllForms(Array.isArray(d.data) ? d.data : []))
      .catch(() => {});
  }, [data.subtype]);

  useEffect(() => {
    if (data.headerType !== 'pdf' && data.subtype !== 'boton_cta') return;
    const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    fetch('/api/templates/custom-fields', { headers: h })
      .then(r => r.ok ? r.json() : [])
      .then(d => setDocFields(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [data.headerType, data.subtype]);

  // WhatsApp solo permite header de texto en listas. Si una config previa quedó
  // con un header de media (imagen/video/pdf) en una lista, lo normalizamos a "ninguno".
  useEffect(() => {
    if (data.subtype === 'list' && ['image', 'video', 'pdf'].includes(data.headerType)) {
      onChange({ headerType: 'none' });
    }
  }, [data.subtype, data.headerType]); // eslint-disable-line react-hooks/exhaustive-deps

  const addItem = () => {
    if ((data.items || []).length >= 10) return;
    onChange({ items: [...(data.items || []), { id: '', title: '', description: '' }] });
  };
  const removeItem = (idx) => { const a = [...(data.items || [])]; a.splice(idx, 1); onChange({ items: a }); };
  const updateItem = (idx, field, val) => { const a = [...(data.items || [])]; a[idx] = { ...a[idx], [field]: val }; onChange({ items: a }); };

  const addButton = () => {
    if ((data.buttons || []).length >= 3) return;
    onChange({ buttons: [...(data.buttons || []), { id: '', title: '' }] });
  };
  const removeButton = (idx) => { const a = [...(data.buttons || [])]; a.splice(idx, 1); onChange({ buttons: a }); };
  const updateButton = (idx, field, val) => { const a = [...(data.buttons || [])]; a[idx] = { ...a[idx], [field]: val }; onChange({ buttons: a }); };
  const addCard = () => { if ((data.cards || []).length >= 10) return; onChange({ cards: [...(data.cards || []), { headerUrl: '', body: '', buttons: [{ id: '', title: 'Ver' }] }] }); };
  const removeCard = (idx) => { const a = [...(data.cards || [])]; a.splice(idx, 1); onChange({ cards: a }); };
  const updateCard = (idx, field, val) => { const a = [...(data.cards || [])]; a[idx] = { ...a[idx], [field]: val }; onChange({ cards: a }); };
  const addCardButton = (ci) => { const a = [...(data.cards || [])]; const btns = a[ci].buttons || []; if (btns.length >= 2) return; a[ci] = { ...a[ci], buttons: [...btns, { id: '', title: '' }] }; onChange({ cards: a }); };
  const removeCardButton = (ci, bi) => { const a = [...(data.cards || [])]; const btns = [...(a[ci].buttons || [])]; btns.splice(bi, 1); a[ci] = { ...a[ci], buttons: btns }; onChange({ cards: a }); };
  const updateCardButton = (ci, bi, field, val) => { const a = [...(data.cards || [])]; const btns = [...(a[ci].buttons || [])]; btns[bi] = { ...btns[bi], [field]: val }; a[ci] = { ...a[ci], buttons: btns }; onChange({ cards: a }); };
  const formatPrice = (price) => { const n = Math.round(Number(price) || 0); return '$' + n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'); };

  const subtype = data.subtype || 'text';

  const headerType = data.headerType || 'none';
  // WhatsApp solo admite header de texto en listas; imagen/video/documento solo en botones y texto.
  const allowMediaHeader = subtype !== 'list';
  const headerFooter = (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>Header (opcional)</label>
        <select
          value={headerType}
          onChange={(e) => onChange({ headerType: e.target.value })}
          style={{ ...inputSt, cursor: 'pointer' }}
        >
          <option value="none">Sin header</option>
          <option value="text">Texto</option>
          {allowMediaHeader && <option value="image">Imagen</option>}
          {allowMediaHeader && <option value="video">Video</option>}
          {allowMediaHeader && <option value="pdf">PDF / Documento</option>}
        </select>
        {headerType === 'text' && (
          <input value={data.header || ''} onChange={(e) => onChange({ header: e.target.value })} maxLength={60} style={{ ...inputSt, marginTop: '6px' }} placeholder="Texto del header (máx 60)" />
        )}
        {allowMediaHeader && (headerType === 'image' || headerType === 'video') && (
          <input value={data.headerUrl || ''} onChange={(e) => onChange({ headerUrl: e.target.value })} style={{ ...inputSt, marginTop: '6px' }} placeholder="URL del archivo (https://...)" />
        )}
        {allowMediaHeader && headerType === 'pdf' && (
          <>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '6px', marginBottom: '4px' }}>
              <label style={checkLabelSt}>
                <input type="radio" name="headerUrlMode" checked={(data.headerUrlMode || 'fixed') === 'fixed'} onChange={() => onChange({ headerUrlMode: 'fixed' })} />
                Link fijo
              </label>
              <label style={checkLabelSt}>
                <input type="radio" name="headerUrlMode" checked={data.headerUrlMode === 'field'} onChange={() => onChange({ headerUrlMode: 'field' })} />
                Campo grabado
              </label>
            </div>
            {(data.headerUrlMode || 'fixed') === 'fixed' ? (
              <input value={data.headerUrl || ''} onChange={e => onChange({ headerUrl: e.target.value })} style={inputSt} placeholder="URL del documento (https://...)" />
            ) : (
              <select value={data.headerUrlField || ''} onChange={e => onChange({ headerUrlField: e.target.value })} style={inputSt}>
                <option value="">— Seleccionar campo —</option>
                {docFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
              </select>
            )}
          </>
        )}
      </div>
      <div style={sectionSt}>
        <label style={checkLabelSt}>
          <input type="checkbox" checked={!!data.useFooter} onChange={(e) => onChange({ useFooter: e.target.checked })} />
          Footer (opcional)
        </label>
        {data.useFooter && <input value={data.footer || ''} onChange={(e) => onChange({ footer: e.target.value })} maxLength={60} style={{ ...inputSt, marginTop: '6px' }} placeholder="Texto del footer (máx 60)" />}
      </div>
    </>
  );

  return (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>Subtipo</label>
        <select value={subtype} onChange={(e) => onChange({ subtype: e.target.value })} style={{ ...inputSt, cursor: 'pointer' }}>
          <option value="text">Texto</option>
          <option value="list">Lista</option>
          <option value="button">Botones</option>
          <option value="product">Producto</option>
          <option value="boton_cta">Botón CTA</option>
          <option value="meta_flow">Flujo Meta</option>
          <option value="carousel">Carrusel</option>
        </select>
      </div>

      {(subtype === 'text') && (
        <>
          <div style={sectionSt}>
            <label style={labelSt}>Texto del mensaje</label>
            <textarea rows={3} value={data.text || ''} onChange={(e) => onChange({ text: e.target.value })} style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          {headerFooter}
        </>
      )}

      {subtype === 'list' && (
        <>
          <div style={sectionSt}>
            <label style={labelSt}>Cuerpo del mensaje (máx 1024)</label>
            <textarea rows={3} maxLength={1024} value={data.text || ''} onChange={(e) => onChange({ text: e.target.value })} style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          <div style={sectionSt}>
            <label style={labelSt}>Texto del botón de lista (máx 20)</label>
            <input value={data.buttonText || ''} onChange={(e) => onChange({ buttonText: e.target.value })} maxLength={20} style={inputSt} placeholder="Ver opciones" />
          </div>
          {headerFooter}
          <div style={sectionSt}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ ...labelSt, margin: 0 }}>Ítems ({(data.items || []).length}/10)</label>
              <button onClick={addItem} disabled={(data.items || []).length >= 10} style={addBtnSt}>+ Agregar</button>
            </div>
            {(data.items || []).map((item, idx) => (
              <div key={idx} style={itemCardSt}>
                <input value={item.title || ''} onChange={(e) => updateItem(idx, 'title', e.target.value)} maxLength={24} style={{ ...inputSt, marginBottom: '4px' }} placeholder="Título * (máx 24)" />
                <input value={item.description || ''} onChange={(e) => updateItem(idx, 'description', e.target.value)} maxLength={72} style={{ ...inputSt, marginBottom: '4px' }} placeholder="Descripción (opcional, máx 72)" />
                <input value={item.id || ''} onChange={(e) => updateItem(idx, 'id', e.target.value)} maxLength={200} style={{ ...inputSt, marginBottom: '6px' }} placeholder="ID de la opción * (máx 200)" />
                <button onClick={() => removeItem(idx)} style={delBtnSt}>Eliminar</button>
              </div>
            ))}
          </div>
        </>
      )}

      {subtype === 'button' && (
        <>
          <div style={sectionSt}>
            <label style={labelSt}>Cuerpo del mensaje (máx 1024)</label>
            <textarea rows={3} maxLength={1024} value={data.text || ''} onChange={(e) => onChange({ text: e.target.value })} style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          {headerFooter}
          <div style={sectionSt}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ ...labelSt, margin: 0 }}>Botones ({(data.buttons || []).length}/3)</label>
              <button onClick={addButton} disabled={(data.buttons || []).length >= 3} style={addBtnSt}>+ Agregar</button>
            </div>
            {(data.buttons || []).map((btn, idx) => (
              <div key={idx} style={itemCardSt}>
                <input value={btn.title || ''} onChange={(e) => updateButton(idx, 'title', e.target.value)} maxLength={20} style={{ ...inputSt, marginBottom: '4px' }} placeholder="Título del botón * (máx 20)" />
                <input value={btn.id || ''} onChange={(e) => updateButton(idx, 'id', e.target.value)} maxLength={256} style={{ ...inputSt, marginBottom: '6px' }} placeholder="ID del botón * (máx 256)" />
                <button onClick={() => removeButton(idx)} style={delBtnSt}>Eliminar</button>
              </div>
            ))}
          </div>
        </>
      )}

      {subtype === 'product' && (
        <>
          <div style={sectionSt}>
            <label style={labelSt}>Mensaje (opcional, soporta [variables])</label>
            <textarea value={data.bodyText || ''} onChange={e => onChange({ bodyText: e.target.value })} style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }} rows={2} placeholder="ej: Estos son nuestros productos disponibles" />
          </div>
          <div style={sectionSt}>
            <label style={labelSt}>Productos seleccionados ({(data.selectedProducts || []).length}/30)</label>
            {(data.selectedProducts || []).map((p, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '4px', marginBottom: '4px', alignItems: 'center' }}>
                <select
                  value={p.pk || ''}
                  onChange={e => {
                    const selected = allProducts.find(pr => String(pr.pk) === e.target.value);
                    const updated = [...(data.selectedProducts || [])];
                    updated[idx] = selected ? { pk: selected.pk, title: selected.title } : { pk: '', title: '' };
                    onChange({ selectedProducts: updated });
                  }}
                  style={{ ...inputSt, flex: 1, padding: '4px 8px' }}
                >
                  <option value="">— Seleccionar producto —</option>
                  {allProducts.map(pr => <option key={pr.pk} value={pr.pk}>{pr.title}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    const updated = (data.selectedProducts || []).filter((_, i) => i !== idx);
                    onChange({ selectedProducts: updated });
                  }}
                  style={{ background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '4px', cursor: 'pointer', padding: '2px 8px', fontSize: '0.8rem' }}
                >×</button>
              </div>
            ))}
            {(data.selectedProducts || []).length < 30 && (
              <button
                type="button"
                onClick={() => onChange({ selectedProducts: [...(data.selectedProducts || []), { pk: '', title: '' }] })}
                style={{ ...addBtnSt, background: '#1e3a8a', color: '#fff', marginTop: '4px' }}
              >+ Agregar producto</button>
            )}
            {allProducts.length === 0 && (
              <p style={{ color: '#94a3b8', fontSize: '0.72rem', marginTop: '6px' }}>
                No tienes productos creados. Ve a Utilidades → Productos primero.
              </p>
            )}
          </div>
        </>
      )}

      {subtype === 'meta_flow' && (
        <>
          <div style={sectionSt}>
            <label style={labelSt}>Formulario a enviar</label>
            <select
              value={data.flow_id || ''}
              onChange={e => {
                const selected = allForms.find(f => f.id === e.target.value);
                onChange({ flow_id: e.target.value, flow_name: selected?.name || '' });
              }}
              style={inputSt}
            >
              <option value="">— Seleccionar formulario —</option>
              {allForms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            {allForms.length === 0 && (
              <a href="/formularios" target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: '#38bdf8', fontSize: '0.72rem', marginTop: '6px', textDecoration: 'none' }}>
                🔗 ¿No tienes formularios? Créalos aquí →
              </a>
            )}
          </div>
          <div style={sectionSt}>
            <label style={labelSt}>Texto del botón (CTA)</label>
            <input value={data.flow_cta || ''} onChange={(e) => onChange({ flow_cta: e.target.value })} style={inputSt} placeholder="ej: Abrir formulario" />
          </div>
        </>
      )}

      {subtype === 'carousel' && (
        <>
          <div style={sectionSt}>
            <label style={labelSt}>Cuerpo del mensaje (máx 1024)</label>
            <textarea rows={2} maxLength={1024} value={data.text || ''} onChange={(e) => onChange({ text: e.target.value })} style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }} placeholder="ej: Selecciona una opción" />
          </div>
          <div style={sectionSt}>
            <label style={labelSt}>Tipo de mensaje</label>
            <select value={data.carouselMode || 'static'} onChange={(e) => onChange({ carouselMode: e.target.value })} style={{ ...inputSt, cursor: 'pointer' }}>
              <option value="static">Estático (manual)</option>
              <option value="product">Producto (automático)</option>
            </select>
          </div>

          {(data.carouselMode || 'static') === 'static' && (
            <div style={sectionSt}>
              <div style={{ marginBottom: '8px' }}>
                <label style={labelSt}>Tipo de media (igual en todas las tarjetas)</label>
                <select value={data.cardMediaType || 'image'} onChange={(e) => onChange({ cardMediaType: e.target.value })} style={{ ...inputSt, cursor: 'pointer' }}>
                  <option value="image">Imagen</option>
                  <option value="video">Video</option>
                </select>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <label style={labelSt}>Tipo de botón (igual en todas las tarjetas)</label>
                <select value={data.cardButtonType || 'quick_reply'} onChange={(e) => onChange({ cardButtonType: e.target.value })} style={{ ...inputSt, cursor: 'pointer' }}>
                  <option value="quick_reply">Respuesta rápida (quick reply)</option>
                  <option value="url">Enlace (URL)</option>
                </select>
                <p style={{ color: '#94a3b8', fontSize: '0.7rem', margin: '4px 0 0' }}>
                  {(data.cardButtonType === 'url')
                    ? 'Cada tarjeta lleva 1 botón con texto y enlace.'
                    : 'Cada tarjeta lleva 1 o 2 botones de respuesta rápida.'}
                </p>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ ...labelSt, margin: 0 }}>Tarjetas ({(data.cards || []).length}/10)</label>
                <button onClick={addCard} disabled={(data.cards || []).length >= 10} style={addBtnSt}>+ Tarjeta</button>
              </div>
              {(data.cards || []).length < 2 && (
                <p style={{ color: '#f59e0b', fontSize: '0.72rem', marginBottom: '6px' }}>Meta exige mínimo 2 tarjetas.</p>
              )}
              {(data.cards || []).map((card, ci) => (
                <div key={ci} style={{ ...itemCardSt, borderColor: '#1e40af' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <strong style={{ color: '#93c5fd', fontSize: '0.78rem' }}>Tarjeta {ci + 1}</strong>
                    <button onClick={() => removeCard(ci)} style={delBtnSt}>Eliminar</button>
                  </div>
                  <input value={card.headerUrl || ''} onChange={(e) => updateCard(ci, 'headerUrl', e.target.value)} style={{ ...inputSt, marginBottom: '4px' }} placeholder={`URL ${data.cardMediaType === 'video' ? 'del video' : 'de la imagen'} (https://...)`} />
                  <textarea rows={2} maxLength={160} value={card.body || ''} onChange={(e) => updateCard(ci, 'body', e.target.value)} style={{ ...inputSt, marginBottom: '6px', resize: 'vertical', fontFamily: 'inherit' }} placeholder="Texto de la tarjeta (máx 160)" />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ ...labelSt, margin: 0 }}>Botones ({(card.buttons || []).length}/{data.cardButtonType === 'url' ? 1 : 2})</span>
                    <button onClick={() => addCardButton(ci)} disabled={(card.buttons || []).length >= (data.cardButtonType === 'url' ? 1 : 2)} style={addBtnSt}>+ Botón</button>
                  </div>
                  {(card.buttons || []).length < 1 && (
                    <p style={{ color: '#f59e0b', fontSize: '0.7rem', marginBottom: '4px' }}>Cada tarjeta necesita mínimo 1 botón.</p>
                  )}
                  {(card.buttons || []).map((btn, bi) => (
                    <div key={bi} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid #334155', borderRadius: '4px', padding: '6px', marginBottom: '4px' }}>
                      <input value={btn.title || ''} onChange={(e) => updateCardButton(ci, bi, 'title', e.target.value)} maxLength={20} style={{ ...inputSt, marginBottom: '4px' }} placeholder="Título del botón (máx 20)" />
                      {data.cardButtonType === 'url' ? (
                        <input value={btn.url || ''} onChange={(e) => updateCardButton(ci, bi, 'url', e.target.value)} maxLength={2000} style={{ ...inputSt, marginBottom: '4px' }} placeholder="Enlace del botón (https://...)" />
                      ) : (
                        <input value={btn.id || ''} onChange={(e) => updateCardButton(ci, bi, 'id', e.target.value)} maxLength={256} style={{ ...inputSt, marginBottom: '4px' }} placeholder="ID del botón (máx 256)" />
                      )}
                      <button onClick={() => removeCardButton(ci, bi)} style={delBtnSt}>Quitar botón</button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {data.carouselMode === 'product' && (
            <>
              <div style={sectionSt}>
                <label style={labelSt}>Texto del botón (se aplica a todas las tarjetas, máx 20)</label>
                <input value={data.productButtonText || ''} onChange={(e) => onChange({ productButtonText: e.target.value })} maxLength={20} style={inputSt} placeholder="Ver" />
              </div>
              <div style={sectionSt}>
                <label style={labelSt}>Productos del carrusel ({(data.selectedProducts || []).length}/10)</label>
                {(data.selectedProducts || []).length < 2 && (
                  <p style={{ color: '#f59e0b', fontSize: '0.72rem', marginBottom: '6px' }}>Meta exige mínimo 2 productos.</p>
                )}
                {(data.selectedProducts || []).map((p, idx) => {
                  const prod = allProducts.find(pr => String(pr.pk) === String(p.pk));
                  return (
                    <div key={idx} style={{ marginBottom: '6px' }}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <select
                          value={p.pk || ''}
                          onChange={(e) => {
                            const selected = allProducts.find(pr => String(pr.pk) === e.target.value);
                            const updated = [...(data.selectedProducts || [])];
                            updated[idx] = selected ? { pk: selected.pk, title: selected.title } : { pk: '', title: '' };
                            onChange({ selectedProducts: updated });
                          }}
                          style={{ ...inputSt, flex: 1, padding: '4px 8px' }}
                        >
                          <option value="">— Seleccionar producto —</option>
                          {allProducts.map(pr => <option key={pr.pk} value={pr.pk}>{pr.title}</option>)}
                        </select>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = (data.selectedProducts || []).filter((_, i) => i !== idx);
                            onChange({ selectedProducts: updated });
                          }}
                          style={{ background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '4px', cursor: 'pointer', padding: '2px 8px', fontSize: '0.8rem' }}
                        >×</button>
                      </div>
                      {prod && (
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '2px', paddingLeft: '2px' }}>
                          {formatPrice(prod.price)}{prod.image_link ? ' · con imagen' : ' · sin imagen ⚠'}
                        </div>
                      )}
                    </div>
                  );
                })}
                {(data.selectedProducts || []).length < 10 && (
                  <button
                    type="button"
                    onClick={() => onChange({ selectedProducts: [...(data.selectedProducts || []), { pk: '', title: '' }] })}
                    style={{ ...addBtnSt, background: '#1e3a8a', color: '#fff', marginTop: '4px' }}
                  >+ Agregar producto</button>
                )}
                {allProducts.length === 0 && (
                  <p style={{ color: '#94a3b8', fontSize: '0.72rem', marginTop: '6px' }}>
                    No tienes productos creados. Ve a Utilidades → Productos primero.
                  </p>
                )}
              </div>
            </>
          )}
        </>
      )}
      {subtype === 'boton_cta' && (
        <>
          <div style={sectionSt}>
            <label style={labelSt}>Cuerpo del mensaje (máx 1024)</label>
            <textarea rows={3} maxLength={1024} value={data.text || ''} onChange={(e) => onChange({ text: e.target.value })} style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          {headerFooter}
          <div style={sectionSt}>
            <label style={labelSt}>Texto del botón (máx 20)</label>
            <input value={data.ctaDisplayText || ''} onChange={(e) => onChange({ ctaDisplayText: e.target.value })} maxLength={20} style={inputSt} placeholder="ej: Ver más" />
          </div>
          <div style={sectionSt}>
            <label style={labelSt}>URL del botón</label>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '6px', marginBottom: '4px' }}>
              <label style={checkLabelSt}>
                <input type="radio" name="ctaUrlMode" checked={(data.ctaUrlMode || 'fixed') === 'fixed'} onChange={() => onChange({ ctaUrlMode: 'fixed' })} />
                Link fijo
              </label>
              <label style={checkLabelSt}>
                <input type="radio" name="ctaUrlMode" checked={data.ctaUrlMode === 'field'} onChange={() => onChange({ ctaUrlMode: 'field' })} />
                Campo grabado
              </label>
            </div>
            {(data.ctaUrlMode || 'fixed') === 'fixed' ? (
              <input value={data.ctaUrl || ''} onChange={(e) => onChange({ ctaUrl: e.target.value })} style={inputSt} placeholder="https://..." />
            ) : (
              <select value={data.ctaUrlField || ''} onChange={e => onChange({ ctaUrlField: e.target.value })} style={inputSt}>
                <option value="">— Seleccionar campo —</option>
                {docFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
              </select>
            )}
          </div>
        </>
      )}
    </>
  );
}

function WaitResponseConfig() {
  return (
    <p style={{ color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center', padding: '0.5rem 0' }}>
      El flujo se pausará hasta que el contacto responda.
    </p>
  );
}

const SWITCH_OPERATORS = [
  { value: 'eq',    label: 'Igual' },
  { value: 'ne',    label: 'No es igual' },
  { value: 'gt',    label: 'Mayor que' },
  { value: 'lt',    label: 'Menor que' },
  { value: 'regex', label: 'Expresión regular' },
];

function SwitchConfig({ data, onChange }) {
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/templates/custom-fields', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setCustomFields)
      .catch(() => {});
  }, []);

  const allCases = data.cases && data.cases.length > 0
    ? data.cases
    : [{ id: 'default', label: 'Por defecto' }];

  const regularCases = allCases.filter(c => c.id !== 'default');

  const ensureDefault = (regular) => [...regular, { id: 'default', label: 'Por defecto' }];

  const addCase = () => {
    const newCase = {
      id: `case-${Date.now()}`,
      label: `Caso ${regularCases.length + 1}`,
      mode: 'lastResponse',
      operator: 'eq',
      value: '',
    };
    onChange({ cases: ensureDefault([...regularCases, newCase]) });
  };

  const removeCase = (idx) => {
    onChange({ cases: ensureDefault(regularCases.filter((_, i) => i !== idx)) });
  };

  const updateCase = (idx, field, val) => {
    onChange({ cases: ensureDefault(regularCases.map((c, i) => i === idx ? { ...c, [field]: val } : c)) });
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <label style={{ ...labelSt, margin: 0 }}>Casos ({regularCases.length})</label>
        <button onClick={addCase} style={{ ...addBtnSt, background: '#ea580c', color: '#fff' }}>+ Caso</button>
      </div>

      {regularCases.map((c, idx) => (
        <div key={c.id} style={itemCardSt}>
          <div style={{ marginBottom: '4px' }}>
            <label style={{ ...labelSt, fontSize: '0.72rem' }}>Modo</label>
            <select value={c.mode || 'lastResponse'} onChange={e => updateCase(idx, 'mode', e.target.value)} style={inputSt}>
              <option value="lastResponse">Último escrito</option>
              <option value="field">Campo grabado</option>
            </select>
          </div>

          {c.mode === 'field' && (
            <div style={{ marginBottom: '4px' }}>
              <label style={{ ...labelSt, fontSize: '0.72rem' }}>Campo personalizado</label>
              <select value={c.field || ''} onChange={e => updateCase(idx, 'field', e.target.value)} style={inputSt}>
                <option value="">— Seleccionar campo —</option>
                {customFields.map(cf => (
                  <option key={cf.id} value={cf.name}>{cf.name}</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: '4px' }}>
            <label style={{ ...labelSt, fontSize: '0.72rem' }}>Operador</label>
            <select value={c.operator || 'eq'} onChange={e => updateCase(idx, 'operator', e.target.value)} style={inputSt}>
              {SWITCH_OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <label style={{ ...labelSt, fontSize: '0.72rem' }}>
              {c.operator === 'regex' ? 'Patrón (regex)' : 'Valor a comparar'}
            </label>
            <input
              value={c.value || ''}
              onChange={e => updateCase(idx, 'value', e.target.value)}
              style={inputSt}
              placeholder={c.operator === 'regex' ? 'ej: ^.+$' : 'ej: 1'}
            />
          </div>

          <div style={{ marginBottom: '6px' }}>
            <label style={{ ...labelSt, fontSize: '0.72rem' }}>Etiqueta (visible en nodo)</label>
            <input
              value={c.label || ''}
              onChange={e => updateCase(idx, 'label', e.target.value)}
              style={inputSt}
              placeholder={`Caso ${idx + 1}`}
            />
          </div>

          <button onClick={() => removeCase(idx)} style={delBtnSt}>Eliminar</button>
        </div>
      ))}

      <div style={{ ...itemCardSt, borderColor: '#ea580c', background: 'rgba(234,88,12,0.06)', textAlign: 'center' }}>
        <div style={{ color: '#fdba74', fontSize: '0.8rem', fontWeight: '700' }}>🔁 Por defecto</div>
        <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginTop: '2px' }}>
          Se ejecuta si ningún caso se cumple
        </div>
      </div>
    </>
  );
}

function SaveToDbConfig({ data, onChange, token }) {
  const [fields, setFields] = useState([]);
  const [lists, setLists] = useState([]);

  useEffect(() => {
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    fetch('/api/templates/custom-fields', { headers: h })
      .then((r) => r.json())
      .then((d) => setFields(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch('/api/marketing/lists', { headers: h })
      .then((r) => r.json())
      .then((d) => setLists(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [token]);

  const SOURCE_OPTIONS = [
    { value: 'last_message', label: 'Último mensaje del cliente' },
    { value: 'current_date', label: 'Fecha actual' },
    { value: 'client_phone', label: 'Celular del cliente' },
    { value: 'wa_name', label: 'Nombre de WhatsApp' },
    { value: 'free', label: 'Valor fijo' },
  ];

  return (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>Campo personalizado</label>
        <select
          value={data.fieldId || ''}
          onChange={(e) => {
            const f = fields.find((x) => x.id === parseInt(e.target.value, 10));
            onChange({ fieldId: e.target.value, fieldName: f?.name || '' });
          }}
          style={{ ...inputSt, cursor: 'pointer' }}
        >
          <option value="">— Seleccionar campo —</option>
          {fields.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Fuente del valor</label>
        <select
          value={data.sourceType || ''}
          onChange={(e) => onChange({ sourceType: e.target.value, freeValue: '' })}
          style={{ ...inputSt, cursor: 'pointer' }}
        >
          <option value="">— Seleccionar fuente —</option>
          {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {data.sourceType === 'free' && (
        <div style={sectionSt}>
          <label style={labelSt}>Valor fijo</label>
          <input
            value={data.freeValue || ''}
            onChange={(e) => onChange({ freeValue: e.target.value })}
            style={inputSt}
            placeholder="Valor a guardar"
          />
        </div>
      )}
      <div style={sectionSt}>
        <label style={labelSt}>Agregar a lista de contactos</label>
        <select
          value={data.listId || ''}
          onChange={(e) => onChange({ listId: e.target.value })}
          style={{ ...inputSt, cursor: 'pointer' }}
        >
          <option value="">— No agregar a lista —</option>
          {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>
    </>
  );
}

function EnvioConfig({ data, onChange, token }) {
  const [flows, setFlows] = useState([]);
  const [nodes, setNodes] = useState([]);

  useEffect(() => {
    if (!token) return;
    fetch('/api/flows', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((res) => setFlows(res.data || []))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!data.targetFlowId || !token) { setNodes([]); return; }
    fetch(`/api/flows/${data.targetFlowId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((flow) => {
        const cfg = flow.config || { nodes: [] };
        setNodes((cfg.nodes || []).map((n) => ({
          id: n.id,
          label: n.data?.text
            ? `[${n.type}] ${n.data.text.slice(0, 40)}`
            : `[${n.type}] ${n.id}`,
        })));
      })
      .catch(() => setNodes([]));
  }, [data.targetFlowId, token]);

  return (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>Flujo destino</label>
        <select
          value={data.targetFlowId || ''}
          onChange={(e) => {
            const f = flows.find((x) => String(x.id) === e.target.value);
            onChange({ targetFlowId: e.target.value, targetFlowName: f?.name || '', targetNodeId: '', targetNodeLabel: '' });
          }}
          style={{ ...inputSt, cursor: 'pointer' }}
        >
          <option value="">— Seleccionar flujo —</option>
          {flows.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>
      {data.targetFlowId && (
        <div style={sectionSt}>
          <label style={labelSt}>Nodo de inicio en ese flujo</label>
          <select
            value={data.targetNodeId || ''}
            onChange={(e) => {
              const n = nodes.find((x) => x.id === e.target.value);
              onChange({ targetNodeId: e.target.value, targetNodeLabel: n?.label || '' });
            }}
            style={{ ...inputSt, cursor: 'pointer' }}
          >
            <option value="">— Nodo raíz (por defecto) —</option>
            {nodes.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
          </select>
        </div>
      )}
    </>
  );
}

function EscalateConfig({ data, onChange, token }) {
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    if (!token) return;
    fetch('/api/campaigns', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((res) => setCampaigns(res.data || []))
      .catch(() => {});
  }, [token]);

  return (
    <div style={sectionSt}>
      <label style={labelSt}>Campaña destino</label>
      <select
        value={data.campaignId || ''}
        onChange={(e) => {
          const c = campaigns.find((x) => String(x.id) === e.target.value);
          onChange({ campaignId: e.target.value, campaignLabel: c?.name || '' });
        }}
        style={{ ...inputSt, cursor: 'pointer' }}
      >
        <option value="">— Seleccionar campaña —</option>
        {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </div>
  );
}

// ── Palette item ──────────────────────────────────────────────────────────────

function PaletteItem({ nodeType, bg, border, titleColor, title, subtitle }) {
  return (
    <div
      draggable
      data-nodetype={nodeType}
      onDragStart={(e) => { e.dataTransfer.setData('application/reactflow', nodeType); e.dataTransfer.effectAllowed = 'move'; }}
      style={{ background: bg, border: `2px solid ${border}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '8px', cursor: 'pointer', userSelect: 'none' }}
    >
      <div style={{ fontSize: '0.68rem', color: titleColor, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</div>
      <div style={{ fontSize: '0.78rem', color: titleColor, opacity: 0.7, marginTop: '2px' }}>{subtitle}</div>
    </div>
  );
}

function PaymentGatewayConfig({ data, onChange }) {
  const [hasPasarela, setHasPasarela] = useState(null);
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => {
    const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    fetch('/api/pay/company-info', { headers: h })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setHasPasarela(d.has_pasarela); })
      .catch(() => {});
    fetch('/api/templates/custom-fields', { headers: h })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomFields(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  if (hasPasarela === null) {
    return <p style={{ color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center' }}>Cargando...</p>;
  }

  if (!hasPasarela) {
    return (
      <div style={{ background: 'rgba(167,139,250,0.08)', border: '1px solid #7c3aed', borderRadius: '8px', padding: '0.8rem' }}>
        <p style={{ color: '#a78bfa', fontSize: '0.82rem', lineHeight: '1.5', margin: '0 0 6px' }}>
          ⚠️ Este nodo solo está disponible para administradores que hayan completado todos los campos de su empresa, incluyendo la clave de pasarela de pagos.
        </p>
        <a href="/empresa" target="_blank" rel="noopener noreferrer" style={{ color: '#38bdf8', fontSize: '0.78rem', textDecoration: 'none' }}>
          → Ir a configurar mi empresa
        </a>
      </div>
    );
  }

  return (
    <>
      <div style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid #38bdf8', borderRadius: '8px', padding: '0.8rem', marginBottom: '0.9rem' }}>
        <p style={{ color: '#38bdf8', fontSize: '0.8rem', lineHeight: '1.5', margin: '0 0 6px', fontWeight: 600 }}>
          ⚠️ Importante: para que los pagos queden registrados automáticamente
        </p>
        <p style={{ color: '#cbd5e1', fontSize: '0.76rem', lineHeight: '1.5', margin: '0 0 6px' }}>
          Si quieres que todo quede registrado (el pago y el estado del pedido), agrega esta URL en la sección de <strong>Eventos</strong> de tu cuenta de Wompi (Desarrollo → Programadores → URL de Eventos):
        </p>
        <code
          onClick={(e) => { const r = document.createRange(); r.selectNodeContents(e.currentTarget); const s = window.getSelection(); s.removeAllRanges(); s.addRange(r); }}
          style={{ display: 'block', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', padding: '7px 9px', color: '#7dd3fc', fontSize: '0.74rem', wordBreak: 'break-all', cursor: 'text', userSelect: 'all' }}
        >
          https://app.newhorizontstart.com/api/pay/client-webhook
        </code>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Campo con el monto a cobrar</label>
        <select value={data.amountField || ''} onChange={e => onChange({ amountField: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar campo —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Campo donde guardar URL de pago</label>
        <select value={data.urlField || ''} onChange={e => onChange({ urlField: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar campo —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Mensaje si el pago es aceptado</label>
        <textarea value={data.messageAccepted || ''} onChange={e => onChange({ messageAccepted: e.target.value })} style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }} rows={3} placeholder="ej: ¡Tu pago fue exitoso! Gracias por tu compra." />
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Mensaje si el pago es rechazado</label>
        <textarea value={data.messageRejected || ''} onChange={e => onChange({ messageRejected: e.target.value })} style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }} rows={3} placeholder="ej: Tu pago fue rechazado. Por favor intenta con otro método." />
      </div>
    </>
  );
}

function CreatePdfConfig({ data, onChange }) {
  const [pdfTemplates, setPdfTemplates] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => {
    const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    fetch('/api/pdf-templates?page=1', { headers: h })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setPdfTemplates(Array.isArray(d.data) ? d.data : []))
      .catch(() => {});
    fetch('/api/templates/custom-fields', { headers: h })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomFields(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  return (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>Plantilla PDF</label>
        <select
          value={data.templateId || ''}
          onChange={e => {
            const selected = pdfTemplates.find(t => t.id === Number(e.target.value));
            onChange({ templateId: Number(e.target.value) || null, templateName: selected?.nombre || '' });
          }}
          style={inputSt}
        >
          <option value="">— Seleccionar plantilla —</option>
          {pdfTemplates.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
        </select>
        {pdfTemplates.length === 0 && (
          <a
            href="/plantillas-pdf/nueva"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'block', color: '#f59e0b', fontSize: '0.72rem', marginTop: '6px', textDecoration: 'none', lineHeight: '1.4' }}
          >
            🔗 ¿No tienes templates? Créalas aquí →
          </a>
        )}
      </div>

      <div style={sectionSt}>
        <label style={labelSt}>Guardar URL en campo</label>
        <select
          value={data.fieldName || ''}
          onChange={e => onChange({ fieldName: e.target.value })}
          style={inputSt}
        >
          <option value="">— Seleccionar campo —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
        <p style={{ color: '#94a3b8', fontSize: '0.7rem', marginTop: '4px', margin: '4px 0 0' }}>
          La URL del PDF generado se guardará en este campo del contacto.
        </p>
      </div>
    </>
  );
}

function SendEmailConfig({ data, onChange }) {
  const [customFields, setCustomFields] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);

  useEffect(() => {
    const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    fetch('/api/templates/custom-fields', { headers: h })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomFields(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch('/api/email-templates?page=1', { headers: h })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setEmailTemplates(Array.isArray(d.data) ? d.data : []))
      .catch(() => {});
  }, []);

  return (
    <>
      <div style={{ background: 'rgba(52, 211, 153, 0.08)', border: '1px solid #059669', borderRadius: '6px', padding: '8px', fontSize: '0.72rem', color: '#6ee7b7', lineHeight: '1.5', marginBottom: '0.75rem' }}>
        ⚠️ Este nodo puede enviar un máximo de <strong>100 correos por día</strong>.
      </div>

      <div style={sectionSt}>
        <label style={labelSt}>Proveedor</label>
        <select value={data.provider || 'gmail'} onChange={e => onChange({ provider: e.target.value })} style={inputSt}>
          <option value="gmail">Gmail</option>
          <option value="outlook">Outlook</option>
          <option value="office365">Office 365</option>
          <option value="sendgrid">SendGrid</option>
        </select>
      </div>

      <div style={sectionSt}>
        <label style={labelSt}>Correo remitente</label>
        <input type="email" name="flow-from-email" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} value={data.fromEmail || ''} onChange={e => onChange({ fromEmail: e.target.value })} style={inputSt} placeholder="tu@gmail.com" />
      </div>

      <div style={sectionSt}>
        <label style={labelSt}>Contraseña API</label>
        <input type="password" name="flow-api-password" autoComplete="new-password" value={data.apiPassword || ''} onChange={e => onChange({ apiPassword: e.target.value })} style={inputSt} placeholder="contraseña de aplicación" />
        <a href="https://www.youtube.com/watch?v=_Ep3ttp1Z3w" target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: '#38bdf8', fontSize: '0.72rem', marginTop: '4px', textDecoration: 'none' }}>
          🎬 ¿No sabes cómo generarla? Ver video →
        </a>
      </div>

      <div style={sectionSt}>
        <label style={labelSt}>Destino del correo</label>
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '6px' }}>
          <label style={checkLabelSt}>
            <input type="radio" name="destMode" checked={(data.destMode || 'fixed') === 'fixed'} onChange={() => onChange({ destMode: 'fixed' })} />
            Correo fijo
          </label>
          <label style={checkLabelSt}>
            <input type="radio" name="destMode" checked={data.destMode === 'field'} onChange={() => onChange({ destMode: 'field' })} />
            Campo grabado
          </label>
        </div>
        {(data.destMode || 'fixed') === 'fixed' ? (
          <input type="email" value={data.destEmail || ''} onChange={e => onChange({ destEmail: e.target.value })} style={inputSt} placeholder="destinatario@email.com" />
        ) : (
          <select value={data.destField || ''} onChange={e => onChange({ destField: e.target.value })} style={inputSt}>
            <option value="">— Seleccionar campo —</option>
            {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
          </select>
        )}
      </div>

      <div style={sectionSt}>
        <label style={labelSt}>Asunto (soporta [variables])</label>
        <input type="text" value={data.subject || ''} onChange={e => onChange({ subject: e.target.value })} style={inputSt} placeholder="ej: Hola [nombre], tu cita es [fecha]" />
      </div>

      <div style={sectionSt}>
        <label style={labelSt}>Contenido</label>
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '6px' }}>
          <label style={checkLabelSt}>
            <input type="radio" name="contentMode" checked={(data.contentMode || 'text') === 'text'} onChange={() => onChange({ contentMode: 'text' })} />
            Texto libre
          </label>
          <label style={checkLabelSt}>
            <input type="radio" name="contentMode" checked={data.contentMode === 'template'} onChange={() => onChange({ contentMode: 'template' })} />
            Plantilla
          </label>
        </div>
        {(data.contentMode || 'text') === 'text' ? (
          <textarea
            value={data.bodyText || ''}
            onChange={e => onChange({ bodyText: e.target.value })}
            style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }}
            rows={5}
            placeholder="Escribe el contenido del correo. Usa [variables] para personalizar."
          />
        ) : (
          <>
            <select value={data.templateId || ''} onChange={e => onChange({ templateId: Number(e.target.value) || null })} style={inputSt}>
              <option value="">— Seleccionar plantilla —</option>
              {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
            {emailTemplates.length === 0 && (
              <a
                href="/plantillas-email/nueva"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'block', color: '#34d399', fontSize: '0.72rem', marginTop: '6px', textDecoration: 'none', lineHeight: '1.4' }}
              >
                🎨 No tienes templates, no te preocupes — da clic aquí y crea tu template llamativa para tus clientes →
              </a>
            )}
          </>
        )}
      </div>
    </>
  );
}

function DomicilioConfig({ data, onChange }) {
  const [customFields, setCustomFields] = useState([]);
  useEffect(() => {
    fetch('/api/templates/custom-fields', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomFields(Array.isArray(d) ? d : []));
  }, []);
  return (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>Campo con el departamento del cliente</label>
        <select value={data.dept_field || ''} onChange={e => onChange({ dept_field: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar campo —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Campo donde guardar el valor del domicilio</label>
        <select value={data.fee_field || ''} onChange={e => onChange({ fee_field: e.target.value })} style={inputSt}>
          <option value="">— No guardar —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '0.72rem', color: '#94a3b8', lineHeight: '1.5' }}>
        Consulta la tabla de precios de domicilio de tu empresa y actualiza el pedido más reciente del cliente.
      </div>
    </>
  );
}

function DireccionConfig({ data, onChange }) {
  const [customFields, setCustomFields] = useState([]);
  useEffect(() => {
    fetch('/api/templates/custom-fields', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomFields(Array.isArray(d) ? d : []));
  }, []);
  return (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>Campo donde está guardada la dirección</label>
        <select value={data.address_field || ''} onChange={e => onChange({ address_field: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar campo —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '0.72rem', color: '#94a3b8', lineHeight: '1.5' }}>
        Lee el campo seleccionado y actualiza la dirección en el pedido más reciente del cliente.
      </div>
    </>
  );
}

function CalculatorConfig({ data, onChange }) {
  const [customFields, setCustomFields] = useState([]);
  useEffect(() => {
    fetch('/api/templates/custom-fields', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomFields(Array.isArray(d) ? d : []));
  }, []);
  return (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>Campo 1 (primer operando)</label>
        <select value={data.field1 || ''} onChange={e => onChange({ field1: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Operación</label>
        <select value={data.operation || 'suma'} onChange={e => onChange({ operation: e.target.value })} style={inputSt}>
          <option value="suma">Suma (+)</option>
          <option value="resta">Resta (−)</option>
          <option value="multiplicacion">Multiplicación (×)</option>
          <option value="division">División (÷)</option>
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Campo 2 (segundo operando)</label>
        <select value={data.field2 || ''} onChange={e => onChange({ field2: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Guardar resultado en campo</label>
        <select value={data.result_field || ''} onChange={e => onChange({ result_field: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '0.72rem', color: '#94a3b8', lineHeight: '1.5' }}>
        Los valores se parsean a número antes de operar y el resultado se guarda como texto.
      </div>
    </>
  );
}

function BookAppointmentConfig({ data, onChange }) {
  const [services, setServices] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => {
    const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    fetch('/api/services?page=1', { headers: h })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setServices(d.data || []));
    fetch('/api/templates/custom-fields', { headers: h })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomFields(Array.isArray(d) ? d : []));
  }, []);

  return (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>Servicio a reservar</label>
        <select value={data.service_id || ''} onChange={e => onChange({ service_id: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar servicio —</option>
          {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.time} min)</option>)}
        </select>
        {services.length === 0 && (
          <a href="/servicios" target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: '#f59e0b', fontSize: '0.72rem', marginTop: '6px', textDecoration: 'none' }}>
            🔗 ¿No tienes servicios? Créalos aquí →
          </a>
        )}
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Campo con la fecha (YYYY-MM-DD)</label>
        <select value={data.date_field || ''} onChange={e => onChange({ date_field: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar campo —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Campo con la hora (HH:MM)</label>
        <select value={data.time_field || ''} onChange={e => onChange({ time_field: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar campo —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Nombre del cliente</label>
        <select value={data.name_field || 'wa_name'} onChange={e => onChange({ name_field: e.target.value })} style={inputSt}>
          <option value="wa_name">Nombre de WhatsApp</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '0.72rem', color: '#94a3b8', lineHeight: '1.5' }}>
        Salida <strong style={{ color: '#22c55e' }}>Éxito</strong>: cita reservada correctamente.<br />
        Salida <strong style={{ color: '#f87171' }}>Fallo</strong>: horario no disponible o datos incompletos.
      </div>
    </>
  );
}

function TicketsConfig({ data, onChange }) {
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => {
    const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    fetch('/api/templates/custom-fields', { headers: h })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomFields(Array.isArray(d) ? d : []));
  }, []);

  return (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>Campo del título</label>
        <select value={data.title_field || ''} onChange={e => onChange({ title_field: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar campo —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Campo de la descripción</label>
        <select value={data.description_field || ''} onChange={e => onChange({ description_field: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar campo —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Campo del nombre</label>
        <select value={data.name_field || ''} onChange={e => onChange({ name_field: e.target.value })} style={inputSt}>
          <option value="">Nombre de WhatsApp</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Guardar el ID del ticket en</label>
        <select value={data.id_field || ''} onChange={e => onChange({ id_field: e.target.value })} style={inputSt}>
          <option value="">— No guardar —</option>
          {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
        </select>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '0.72rem', color: '#94a3b8', lineHeight: '1.5' }}>
        Al pasar por aquí se graba un ticket de soporte con los datos guardados del contacto. La plantilla del ticket se configura en <strong style={{ color: '#fdba74' }}>Empresa → Tickets</strong>.
      </div>
    </>
  );
}

function AvailableSlotsConfig({ data, onChange }) {
  const [services, setServices] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => {
    const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    fetch('/api/services?page=1', { headers: h })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setServices(d.data || []));
    fetch('/api/templates/custom-fields', { headers: h })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomFields(Array.isArray(d) ? d : []));
  }, []);

  return (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>Servicio</label>
        <select value={data.service_id || ''} onChange={e => onChange({ service_id: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar servicio —</option>
          {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.time} min)</option>)}
        </select>
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Fecha a consultar</label>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '6px' }}>
          <label style={checkLabelSt}>
            <input type="radio" checked={(data.date_source || 'today') === 'today'} onChange={() => onChange({ date_source: 'today' })} />
            Hoy
          </label>
          <label style={checkLabelSt}>
            <input type="radio" checked={data.date_source === 'field'} onChange={() => onChange({ date_source: 'field' })} />
            Desde campo
          </label>
        </div>
        {data.date_source === 'field' && (
          <select value={data.date_field || ''} onChange={e => onChange({ date_field: e.target.value })} style={inputSt}>
            <option value="">— Seleccionar campo —</option>
            {customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>)}
          </select>
        )}
      </div>
      <div style={sectionSt}>
        <label style={labelSt}>Texto cuando no hay horarios</label>
        <textarea
          value={data.no_slots_text || ''}
          onChange={e => onChange({ no_slots_text: e.target.value })}
          style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }}
          rows={2}
          placeholder="No hay horarios disponibles para este día."
        />
      </div>
    </>
  );
}

// ── Default node data ─────────────────────────────────────────────────────────

// Hook compartido por los nodos nuevos: trae los campos personalizados del tenant
// para poblar los selectores.
function useCustomFields() {
  const [customFields, setCustomFields] = useState([]);
  useEffect(() => {
    fetch('/api/templates/custom-fields', { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomFields(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);
  return customFields;
}

function SmartBoxConfig({ data, onChange }) {
  const customFields = useCustomFields();
  const fieldOptions = customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>);

  return (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>La pregunta sale de</label>
        <select value={data.question_source || 'last_message'} onChange={e => onChange({ question_source: e.target.value })} style={inputSt}>
          <option value="last_message">El último mensaje del cliente</option>
          <option value="field">Un campo personalizado</option>
        </select>
      </div>

      {data.question_source === 'field' && (
        <div style={sectionSt}>
          <label style={labelSt}>Campo con la pregunta</label>
          <select value={data.question_field || ''} onChange={e => onChange({ question_field: e.target.value })} style={inputSt}>
            <option value="">— Seleccionar —</option>
            {fieldOptions}
          </select>
        </div>
      )}

      <div style={{ ...itemCardSt, marginBottom: '0.9rem' }}>
        <label style={{ ...labelSt, color: '#f0abfc', fontWeight: 700 }}>Memoria de la conversación</label>
        <p style={{ color: '#94a3b8', fontSize: '0.71rem', margin: '0 0 8px', lineHeight: '1.45' }}>
          Tres campos donde se guardan los últimos tres turnos (pregunta + respuesta). Rotan solos:
          el turno nuevo entra en el 1 y el más viejo se cae del 3. Son de uso interno del nodo,
          no los uses para mostrar nada.
        </p>
        <label style={labelSt}>Campo pregunta 1 (más reciente)</label>
        <select value={data.history_field_1 || ''} onChange={e => onChange({ history_field_1: e.target.value })} style={{ ...inputSt, marginBottom: '6px' }}>
          <option value="">— Seleccionar —</option>
          {fieldOptions}
        </select>
        <label style={labelSt}>Campo pregunta 2</label>
        <select value={data.history_field_2 || ''} onChange={e => onChange({ history_field_2: e.target.value })} style={{ ...inputSt, marginBottom: '6px' }}>
          <option value="">— Seleccionar —</option>
          {fieldOptions}
        </select>
        <label style={labelSt}>Campo pregunta 3 (más antiguo)</label>
        <select value={data.history_field_3 || ''} onChange={e => onChange({ history_field_3: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar —</option>
          {fieldOptions}
        </select>
      </div>

      <div style={sectionSt}>
        <label style={labelSt}>Guardar la respuesta de la IA en *</label>
        <select value={data.answer_field || ''} onChange={e => onChange({ answer_field: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar —</option>
          {fieldOptions}
        </select>
      </div>

      <div style={sectionSt}>
        <label style={labelSt}>Instrucciones para la IA (opcional)</label>
        <textarea
          value={data.system_prompt || ''}
          onChange={e => onChange({ system_prompt: e.target.value })}
          rows={4}
          placeholder="Ej. Eres el asistente de una barbería. Responde corto y amable. Si preguntan por precios, di que se confirman en el local."
          style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '0.9rem' }}>
        <div style={{ flex: 2 }}>
          <label style={labelSt}>Modelo</label>
          <input
            type="text"
            value={data.model || ''}
            onChange={e => onChange({ model: e.target.value })}
            placeholder="minimax/minimax-m3:free"
            style={inputSt}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelSt}>Máx. tokens</label>
          <input
            type="number"
            min="1"
            onWheel={e => e.currentTarget.blur()}
            value={data.max_tokens || ''}
            onChange={e => onChange({ max_tokens: e.target.value })}
            placeholder="400"
            style={inputSt}
          />
        </div>
      </div>

      <div style={{ background: 'rgba(250,204,21,0.07)', border: '1px solid #a16207', borderRadius: '6px', padding: '8px', fontSize: '0.72rem', color: '#fde68a', lineHeight: '1.5', marginBottom: '0.9rem' }}>
        No uses <code>openrouter/free</code> como modelo: ese alias cae en un modelo gratuito
        distinto en cada llamada y algunos no sirven para conversar (uno responde
        &quot;User Safety: safe&quot; y otro devuelve la respuesta vacía). Deja un modelo fijo.
      </div>
      <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '0.72rem', color: '#94a3b8', lineHeight: '1.5' }}>
        El nodo NO le escribe al cliente: solo deja la respuesta en el campo elegido. Para mostrarla,
        conecta después un nodo Enviar Mensaje y escribe el nombre del campo entre corchetes.
        Si la API falla, el campo queda VACÍO (para que el bot no repita la respuesta anterior);
        puedes detectarlo con un nodo Tomar decisión.
      </div>
    </>
  );
}

function ApiRestConfig({ data, onChange }) {
  const customFields = useCustomFields();
  const fieldOptions = customFields.map(cf => <option key={cf.id} value={cf.name}>{cf.name}</option>);

  const bodyType = data.bodyType || 'none';

  // Listas dinámicas de pares clave/valor (headers y form-data).
  const rows = (listKey) => data[listKey] || [];
  const addRow = (listKey) => onChange({ [listKey]: [...rows(listKey), { key: '', value: '' }] });
  const removeRow = (listKey, idx) => onChange({ [listKey]: rows(listKey).filter((_, i) => i !== idx) });
  const updateRow = (listKey, idx, prop, val) => onChange({
    [listKey]: rows(listKey).map((r, i) => (i === idx ? { ...r, [prop]: val } : r)),
  });

  const renderKeyValueList = (listKey, label, keyPlaceholder, valuePlaceholder) => (
    <div style={sectionSt}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <label style={{ ...labelSt, margin: 0 }}>{label + ' (' + rows(listKey).length + ')'}</label>
        <button onClick={() => addRow(listKey)} style={{ ...addBtnSt, background: '#71717a', color: '#fff' }}>+ Agregar</button>
      </div>
      {rows(listKey).map((r, idx) => (
        <div key={idx} style={itemCardSt}>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '5px' }}>
            <input
              type="text" value={r.key || ''} placeholder={keyPlaceholder}
              onChange={e => updateRow(listKey, idx, 'key', e.target.value)}
              style={{ ...inputSt, flex: 1 }}
            />
            <button onClick={() => removeRow(listKey, idx)} style={delBtnSt}>×</button>
          </div>
          <input
            type="text" value={r.value || ''} placeholder={valuePlaceholder}
            onChange={e => updateRow(listKey, idx, 'value', e.target.value)}
            style={inputSt}
          />
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div style={sectionSt}>
        <label style={labelSt}>Método</label>
        <select value={data.method || 'GET'} onChange={e => onChange({ method: e.target.value })} style={inputSt}>
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
          <option value="DELETE">DELETE</option>
        </select>
      </div>

      <div style={sectionSt}>
        <label style={labelSt}>URL</label>
        <input
          type="text"
          value={data.url || ''}
          onChange={e => onChange({ url: e.target.value })}
          placeholder="https://api.ejemplo.com/clientes/[cedula]"
          style={inputSt}
        />
        <p style={{ color: '#64748b', fontSize: '0.7rem', margin: '4px 0 0' }}>
          Admite variables entre corchetes: se reemplazan por lo que tenga guardado ese contacto.
        </p>
      </div>

      {renderKeyValueList('headers', 'Headers', 'Authorization', 'Bearer [token_cliente]')}

      <div style={sectionSt}>
        <label style={labelSt}>Body</label>
        <select value={bodyType} onChange={e => onChange({ bodyType: e.target.value })} style={inputSt}>
          <option value="none">none (sin body)</option>
          <option value="form-data">form-data</option>
          <option value="raw">raw</option>
        </select>
      </div>

      {bodyType === 'form-data' && renderKeyValueList('formData', 'Campos del form-data', 'nombre_campo', '[nombre_cliente]')}

      {bodyType === 'raw' && (
        <>
          <div style={sectionSt}>
            <label style={labelSt}>Formato</label>
            <select value={data.rawFormat || 'json'} onChange={e => onChange({ rawFormat: e.target.value })} style={inputSt}>
              <option value="json">JSON</option>
              <option value="text">Text</option>
            </select>
          </div>
          <div style={sectionSt}>
            <label style={labelSt}>Contenido</label>
            <textarea
              value={data.rawBody || ''}
              onChange={e => onChange({ rawBody: e.target.value })}
              rows={7}
              placeholder={'{\n  "nombre": "[nombre_cliente]",\n  "celular": "[telefono]",\n  "nota": "[respuesta_ia]"\n}'}
              style={{ ...inputSt, resize: 'vertical', fontFamily: 'monospace', fontSize: '0.78rem' }}
            />
            <p style={{ color: '#64748b', fontSize: '0.7rem', margin: '4px 0 0' }}>
              Puedes meter todas las variables que quieras. En formato JSON los valores se escapan
              solos, así que una respuesta con comillas o saltos de línea no rompe el body.
            </p>
          </div>
        </>
      )}

      <div style={sectionSt}>
        <label style={labelSt}>De la respuesta, guardar</label>
        <select value={data.captureMode || 'full'} onChange={e => onChange({ captureMode: e.target.value })} style={inputSt}>
          <option value="full">Toda la respuesta</option>
          <option value="path">Solo una propiedad</option>
        </select>
      </div>

      {data.captureMode === 'path' && (
        <div style={sectionSt}>
          <label style={labelSt}>Ruta de la propiedad</label>
          <input
            type="text"
            value={data.capturePath || ''}
            onChange={e => onChange({ capturePath: e.target.value })}
            placeholder="response[0].contact.status"
            style={{ ...inputSt, fontFamily: 'monospace' }}
          />
          <p style={{ color: '#64748b', fontSize: '0.7rem', margin: '4px 0 0' }}>
            Notación de punto y corchetes. Si la API devuelve el array directo puedes escribir igual
            response[0]... — cuando el primer tramo no existe, se entiende como la raíz.
          </p>
        </div>
      )}

      <div style={sectionSt}>
        <label style={labelSt}>Guardar en el campo *</label>
        <select value={data.result_field || ''} onChange={e => onChange({ result_field: e.target.value })} style={inputSt}>
          <option value="">— Seleccionar —</option>
          {fieldOptions}
        </select>
      </div>

      <div style={sectionSt}>
        <label style={labelSt}>Tiempo límite (ms)</label>
        <input
          type="number" min="1000"
          onWheel={e => e.currentTarget.blur()}
          value={data.timeout_ms || ''}
          onChange={e => onChange({ timeout_ms: e.target.value })}
          placeholder="20000"
          style={inputSt}
        />
      </div>

      <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '0.72rem', color: '#94a3b8', lineHeight: '1.5' }}>
        El nodo tiene dos salidas: por <span style={{ color: '#22c55e' }}>OK</span> sale si la API
        respondió bien (2xx), y por <span style={{ color: '#f87171' }}>Error</span> si hubo timeout,
        4xx, 5xx o no se pudo conectar. Conecta las dos para que el flujo nunca se quede trancado.
        Si guardas un objeto o un array, se graba como texto JSON.
      </div>
    </>
  );
}

function defaultData(type) {
  if (type === 'sendMessage') return { subtype: 'text', text: '', headerType: 'none', header: '', headerUrl: '', useFooter: false, footer: '', items: [], buttons: [], buttonText: '', ctaDisplayText: '', ctaUrl: '', ctaUrlMode: 'fixed', ctaUrlField: '', catalog_id: '', product_retailer_id: '', flow_id: '', flow_cta: '', carouselMode: 'static', cardMediaType: 'image', cardButtonType: 'quick_reply', cards: [], productButtonText: 'Ver' };
  if (type === 'waitResponse') return { variableName: '' };
  if (type === 'switch') return { variableName: 'lastResponse', cases: [{ id: `case-${Date.now()}`, value: '', label: 'Caso 1' }] };
  if (type === 'saveToDb') return { fieldId: '', fieldName: '', sourceType: '', freeValue: '', listId: '' };
  if (type === 'escalate') return { campaignId: '', campaignLabel: '' };
  if (type === 'envio') return { targetFlowId: '', targetFlowName: '', targetNodeId: '', targetNodeLabel: '' };
  if (type === 'sendEmail') return {
    provider: 'gmail', fromEmail: '', apiPassword: '',
    destMode: 'fixed', destEmail: '', destField: '',
    subject: '', contentMode: 'text', bodyText: '', templateId: null,
  };
  if (type === 'createPdf') return { templateId: null, templateName: '', fieldName: '' };
  if (type === 'paymentGateway') return { amountField: '', urlField: '', messageAccepted: '', messageRejected: '' };
  if (type === 'bookAppointment') return { service_id: '', date_field: '', time_field: '', name_field: 'wa_name' };
  if (type === 'availableSlots') return { service_id: '', date_source: 'today', date_field: '', no_slots_text: 'No hay horarios disponibles para este día.' };
  if (type === 'domicilio') return { dept_field: '', fee_field: '' };
  if (type === 'direccion') return { address_field: '' };
  if (type === 'calculator') return { field1: '', field2: '', operation: 'suma', result_field: '' };
  if (type === 'tickets') return { title_field: '', description_field: '', name_field: '', id_field: '' };
  if (type === 'smartBox') return {
    question_source: 'last_message', question_field: '',
    history_field_1: '', history_field_2: '', history_field_3: '',
    answer_field: '', system_prompt: '', model: 'minimax/minimax-m3:free', max_tokens: 400,
  };
  if (type === 'apiRest') return {
    method: 'GET', url: '', headers: [],
    bodyType: 'none', rawFormat: 'json', rawBody: '', formData: [],
    captureMode: 'full', capturePath: '', result_field: '', timeout_ms: 20000,
  };
  return {};
}

// ── Inner component (needs ReactFlowProvider context) ─────────────────────────

function FlowBuilderInner({ token, flowId }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { screenToFlowPosition } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [flowName, setFlowName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!flowId) return;
    fetch(`/api/flows/${flowId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((flow) => {
        setFlowName(flow.name);
        const cfg = flow.config || { nodes: [], edges: [] };
        setNodes(cfg.nodes || []);
        setEdges(cfg.edges || []);
      })
      .catch(() => setLoadError('No se pudo cargar el flujo'))
      .finally(() => setLoading(false));
  }, [flowId, token, setNodes, setEdges]);

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);
  const onNodeClick = useCallback((_, node) => setSelectedNodeId(node.id), []);
  const onPaneClick = useCallback(() => setSelectedNodeId(null), []);
  // Clic en una conexión → eliminarla (para reconectar a otro nodo)
  const onEdgeClick = useCallback((e, edge) => {
    e.stopPropagation();
    if (window.confirm('¿Eliminar esta conexión?')) {
      setEdges((eds) => eds.filter((ed) => ed.id !== edge.id));
    }
  }, [setEdges]);

  const updateNodeData = useCallback((nodeId, changes) => {
    setNodes((nds) => nds.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, ...changes } } : n));
  }, [setNodes]);

  const onDragOver = useCallback((e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }, []);

  const reactFlowWrapper = useRef(null);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const nodeType = e.dataTransfer.getData('application/reactflow');
    if (!nodeType) return;
    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    setNodes((nds) => nds.concat({ id: `${nodeType}-${Date.now()}`, type: nodeType, position, data: defaultData(nodeType) }));
  }, [screenToFlowPosition, setNodes]);

  // Agregar nodo tocándolo (móvil) o haciéndole clic: el arrastre HTML5 no funciona
  // en pantallas táctiles, así que el nodo se crea en el centro del lienzo visible.
  const addNode = useCallback((nodeType) => {
    if (!nodeType) return;
    const bounds = reactFlowWrapper.current?.getBoundingClientRect();
    const center = bounds
      ? screenToFlowPosition({ x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 })
      : { x: 120, y: 120 };
    setNodes((nds) => {
      // Escalonar para que toques repetidos no queden uno encima del otro.
      const offset = (nds.length % 8) * 28;
      return nds.concat({
        id: `${nodeType}-${Date.now()}`,
        type: nodeType,
        position: { x: center.x + offset, y: center.y + offset },
        data: defaultData(nodeType),
      });
    });
  }, [screenToFlowPosition, setNodes]);

  // Delegación: un toque/clic sobre cualquier item de la paleta (marcado con
  // data-nodetype) agrega ese nodo. Evita cablear un handler en cada PaletteItem.
  const onPaletteClick = useCallback((e) => {
    const item = e.target.closest('[data-nodetype]');
    if (item) addNode(item.getAttribute('data-nodetype'));
  }, [addNode]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await fetch(`/api/flows/${flowId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ config: { nodes, edges } }),
      });
      if (!res.ok) throw new Error();
      setSaveMsg('Guardado correctamente');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const deleteSelected = () => {
    if (!selectedNodeId) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
    setSelectedNodeId(null);
  };

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) || null;

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 64px)', color: theme.colors.textMuted }}>Cargando flujo...</div>;
  if (loadError) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 64px)', color: theme.colors.error }}>{loadError}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Top bar */}
      <div style={{ backgroundColor: theme.colors.surface, borderBottom: `1px solid ${theme.colors.border}`, padding: isMobile ? '0.6rem 0.75rem' : '0.6rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: isMobile ? '0.5rem' : '1rem', flexWrap: isMobile ? 'wrap' : 'nowrap', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => navigate('/flows')} style={{ background: 'transparent', border: `1px solid ${theme.colors.border}`, color: theme.colors.textMuted, borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '0.85rem' }}>
            ← Volver
          </button>
          <h2 style={{ color: theme.colors.primary, fontSize: '1.05rem', margin: 0 }}>{flowName}</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {saveMsg && (
            <span style={{ fontSize: '0.85rem', color: saveMsg.startsWith('Error') ? theme.colors.error : theme.colors.success }}>
              {saveMsg}
            </span>
          )}
          <button onClick={handleSave} disabled={saving} style={{ padding: '7px 22px', backgroundColor: saving ? theme.colors.border : theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontWeight: 700, fontSize: '0.9rem', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* 3-panel body */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left: node palette — en móvil se oculta mientras se configura un nodo para dar espacio al panel derecho */}
        {(!isMobile || !selectedNode) && (
        <div onClick={onPaletteClick} style={{ width: isMobile ? '128px' : '190px', backgroundColor: theme.colors.surface, borderRight: `1px solid ${theme.colors.border}`, padding: isMobile ? '0.6rem' : '1rem', flexShrink: 0, overflowY: 'auto' }}>
          <div style={{ color: theme.colors.textMuted, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.75rem' }}>
            {isMobile ? 'Toca para agregar' : 'Toca o arrastra un nodo'}
          </div>
          <PaletteItem nodeType="sendMessage" bg="#1e3a8a" border="#1e40af" titleColor="#93c5fd" title="Enviar Mensaje" subtitle="SendMessage" />
          <PaletteItem nodeType="waitResponse" bg="#f1f5f9" border="#cbd5e1" titleColor="#475569" title="Esperar Respuesta" subtitle="WaitResponse" />
          <PaletteItem nodeType="switch" bg="#7c2d12" border="#ea580c" titleColor="#fdba74" title="Tomar decisión" subtitle="Switch" />
          <PaletteItem nodeType="saveToDb" bg="#166534" border="#16a34a" titleColor="#86efac" title="Guardar en BD" subtitle="SaveToDb" />
          <PaletteItem nodeType="escalate" bg="#4c1d95" border="#7c3aed" titleColor="#c4b5fd" title="Escalar a Agente" subtitle="Escalate" />
          <PaletteItem nodeType="envio" bg="#1e1b4b" border="#4f46e5" titleColor="#a5b4fc" title="Envío a Flujo" subtitle="Envio" />
          <PaletteItem nodeType="sendEmail" bg="#064e3b" border="#059669" titleColor="#34d399" title="Enviar Email" subtitle="SendEmail" />
          <PaletteItem nodeType="createPdf" bg="#78350f" border="#d97706" titleColor="#fcd34d" title="Crear PDF" subtitle="CreatePdf" />
          <PaletteItem nodeType="paymentGateway" bg="#4c1d95" border="#7c3aed" titleColor="#a78bfa" title="Pasarela de pagos" subtitle="Wompi" />
          <PaletteItem nodeType="bookAppointment" bg="#134e4a" border="#0d9488" titleColor="#5eead4" title="Reservar Cita" subtitle="BookAppointment" />
          <PaletteItem nodeType="availableSlots" bg="#1e3a5f" border="#2563eb" titleColor="#93c5fd" title="Citas Disponibles" subtitle="AvailableSlots" />
          <PaletteItem nodeType="domicilio" bg="#451a03" border="#d97706" titleColor="#fbbf24" title="Domicilio" subtitle="Domicilio" />
          <PaletteItem nodeType="direccion" bg="#2d1b69" border="#7c3aed" titleColor="#ddd6fe" title="Dirección" subtitle="Direccion" />
          <PaletteItem nodeType="calculator" bg="#042f2e" border="#0891b2" titleColor="#67e8f9" title="Calculadora" subtitle="Calculator" />
          <PaletteItem nodeType="tickets" bg="#7c2d12" border="#ea580c" titleColor="#fdba74" title="Crear Ticket" subtitle="Tickets" />
          <PaletteItem nodeType="smartBox" bg="#4a044e" border="#c026d3" titleColor="#f0abfc" title="Caja Inteligente" subtitle="SmartBox" />
          <PaletteItem nodeType="apiRest" bg="#27272a" border="#71717a" titleColor="#d4d4d8" title="API REST" subtitle="ApiRest" />
        </div>
        )}

        {/* Center: canvas */}
        <div ref={reactFlowWrapper} style={{ flex: 1, height: '100%' }} onDragOver={onDragOver} onDrop={onDrop}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onEdgeClick={onEdgeClick}
            edgesFocusable
            deleteKeyCode={['Delete', 'Backspace']}
            nodeTypes={nodeTypes}
            fitView
            style={{ backgroundColor: '#080808' }}
          >
            <Background color="#1e293b" gap={20} />
            <Controls />
          </ReactFlow>
        </div>

        {/* Right: config panel — en móvil solo se muestra cuando hay un nodo seleccionado, para dejar el lienzo al frente */}
        {(!isMobile || selectedNode) && (
        <div style={{ width: isMobile ? '210px' : '270px', backgroundColor: theme.colors.surface, borderLeft: `1px solid ${theme.colors.border}`, padding: isMobile ? '0.6rem' : '1rem', flexShrink: 0, overflowY: 'auto' }}>
          {!selectedNode ? (
            <div style={{ color: theme.colors.textMuted, fontSize: '0.85rem', textAlign: 'center', marginTop: '2.5rem', lineHeight: '1.5' }}>
              Selecciona un nodo<br />para configurarlo
            </div>
          ) : (
            <>
              <div style={{ color: theme.colors.primary, fontSize: '0.88rem', fontWeight: 700, marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: `1px solid ${theme.colors.border}` }}>
                Configurar Nodo
              </div>
              {selectedNode.type === 'sendMessage' && (
                <SendMessageConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'waitResponse' && (
                <WaitResponseConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'switch' && (
                <SwitchConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'saveToDb' && (
                <SaveToDbConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} token={token} />
              )}
              {selectedNode.type === 'escalate' && (
                <EscalateConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} token={token} />
              )}
              {selectedNode.type === 'envio' && (
                <EnvioConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} token={token} />
              )}
              {selectedNode.type === 'sendEmail' && (
                <SendEmailConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'createPdf' && (
                <CreatePdfConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'paymentGateway' && (
                <PaymentGatewayConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'bookAppointment' && (
                <BookAppointmentConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'tickets' && (
                <TicketsConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'availableSlots' && (
                <AvailableSlotsConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'domicilio' && (
                <DomicilioConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'direccion' && (
                <DireccionConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'calculator' && (
                <CalculatorConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'smartBox' && (
                <SmartBoxConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              {selectedNode.type === 'apiRest' && (
                <ApiRestConfig data={selectedNode.data} onChange={(c) => updateNodeData(selectedNodeId, c)} />
              )}
              <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: `1px solid ${theme.colors.border}` }}>
                <button onClick={deleteSelected} style={{ width: '100%', padding: '7px', background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer' }}>
                  Eliminar nodo
                </button>
              </div>
            </>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

// ── Exported component ────────────────────────────────────────────────────────

function FlowBuilder({ token }) {
  const { id } = useParams();
  return (
    <ReactFlowProvider>
      <FlowBuilderInner token={token} flowId={id} />
    </ReactFlowProvider>
  );
}

export default FlowBuilder;
